import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { initializePushNotifications, unregisterPushNotifications, getCurrentDeviceToken } from '../services/pushNotificationService';

// Site URL for OAuth redirects (CRA uses process.env, not import.meta.env)
const SITE_URL =
  process.env.REACT_APP_SITE_URL || // e.g. https://www.appsytec.com
  window.location.origin;            // fallback for local dev if env is not set

const SupabaseAuthContext = createContext();

export const useSupabaseAuth = () => {
  const context = useContext(SupabaseAuthContext);
  if (!context) {
    throw new Error('useSupabaseAuth must be used within a SupabaseAuthProvider');
  }
  return context;
};

export const SupabaseAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchUserRole(session.user.id);
      } else {
        setLoading(false);
      }
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchUserRole(session.user.id);
      } else {
        setUserRole(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchUserRole = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('Error fetching user role:', error);
        setUserRole(null);
      } else {
        const role = data?.role || null;
        setUserRole(role);

        // If user is a candidate, run reconciliation for pending applications
        if (role === 'candidate') {
          const { data: { user: authUser } } = await supabase.auth.getUser();
          if (authUser?.email) {
            console.log('Running application reconciliation for candidate:', authUser.email);

            // First, ensure candidate record has user_id linked
            const { data: candidate } = await supabase
              .from('candidates')
              .select('id, user_id')
              .eq('email', authUser.email)
              .maybeSingle();

            if (candidate && !candidate.user_id) {
              console.log('Linking candidate to user_id:', authUser.id);
              await supabase
                .from('candidates')
                .update({ user_id: authUser.id })
                .eq('id', candidate.id);
            }

            // Run reconciliation for pending applications
            const { data: reconcileResult, error: reconcileError } = await supabase.rpc(
              'reconcile_pending_applications',
              {
                p_auth_user_id: userId,
                p_email: authUser.email
              }
            );

            if (reconcileError) {
              console.error('Error reconciling applications:', reconcileError);
            } else if (reconcileResult && reconcileResult.reconciled_count > 0) {
              console.log(`Reconciled ${reconcileResult.reconciled_count} pending application(s)`);
            }
          }
        }

        // Initialize push notifications for mobile (if available)
        if (userId) {
          try {
            await initializePushNotifications(userId);
            console.log('[Auth] Push notifications initialized for user:', userId);
          } catch (pushError) {
            console.error('[Auth] Push notification init error:', pushError);
            // Don't fail auth if push notifications fail
          }
        }
      }
    } catch (error) {
      console.error('Error in fetchUserRole:', error);
      setUserRole(null);
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      console.error('Sign in error:', error);
      return { success: false, error: error.message };
    }
  };

  const signOut = async () => {
    try {
      // Unregister push notifications before signing out
      try {
        const deviceToken = await getCurrentDeviceToken();
        if (deviceToken) {
          await unregisterPushNotifications(deviceToken);
          console.log('[Auth] Push notifications unregistered');
        }
      } catch (pushError) {
        console.error('[Auth] Push notification cleanup error:', pushError);
        // Don't fail logout if push cleanup fails
      }

      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      // Clear persisted filter settings on logout
      localStorage.removeItem('rfqs_filter_settings');

      setUserRole(null);
      return { success: true };
    } catch (error) {
      console.error('Sign out error:', error);
      return { success: false, error: error.message };
    }
  };

  const signUp = async (email, password, metadata = {}) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata,
        },
      });

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      console.error('Sign up error:', error);
      return { success: false, error: error.message };
    }
  };

  const signInWithOAuth = async (provider) => {
    try {
      const oauthOptions = {
        redirectTo: `${SITE_URL}/auth/callback`,
      };

      // LinkedIn OIDC requires explicit scopes to get name + email
      if (provider === 'linkedin_oidc') {
        oauthOptions.scopes = 'openid profile email';
      }

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: provider,
        options: oauthOptions,
      });

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      console.error('OAuth sign in error:', error);
      return { success: false, error: error.message };
    }
  };

  // Allow AuthCallback to re-fetch role after creating it via RPC
  const refreshRole = async (userId) => {
    const { data, error } = await supabase
      .from('user_roles')
      .select('role')
      .eq('id', userId)
      .single();

    if (!error && data?.role) {
      setUserRole(data.role);
    }
  };

  const value = {
    user,
    userRole,
    session,
    loading,
    signIn,
    signOut,
    signUp,
    signInWithOAuth,
    refreshRole,
    isAuthenticated: !!user,
    isRecruiter: userRole === 'recruiter' || userRole === 'admin',
    isClient: userRole === 'client',
    isCandidate: userRole === 'candidate',
    isAdmin: userRole === 'admin',
  };

  return (
    <SupabaseAuthContext.Provider value={value}>
      {children}
    </SupabaseAuthContext.Provider>
  );
};
